import React, { useMemo } from 'react';
import { formatCurrency } from '../utils/formatters';
const ORDER = ['Total Revenue', 'Total Expenses', 'Gross Profit', 'Net Income'];


function computeKpis(points) {
  // For each metric, find the last actual and the first forecast
  const byMetric = {};
  for (const p of points) {
    if (!byMetric[p.metric]) {
      byMetric[p.metric] = { lastActual: null, nextForecast: null, nextForecastPeriod: null };
    }
  }

  const actuals = points.filter(p => !p.is_forecast && typeof p.actual === 'number');
  const forecasts = points.filter(p => p.is_forecast && typeof p.predicted === 'number');

  const periodOrder = [...new Set(points.map(p => p.period))];
  const periodIndex = new Map(periodOrder.map((per, idx) => [per, idx]));

  // Last actual per metric (by latest period order)
  for (const a of actuals) {
    const curr = byMetric[a.metric];
    if (!curr) continue;
    const idx = periodIndex.get(a.period) ?? -1;
    const prevIdx = curr._lastIdx ?? -1;
    if (idx > prevIdx) {
      curr.lastActual = a.actual;
      curr._lastIdx = idx;
    }
  }

  // First forecast per metric (earliest forecast period)
  for (const f of forecasts) {
    const curr = byMetric[f.metric];
    if (!curr) continue;
    const idx = periodIndex.get(f.period) ?? 999999;
    const prevIdx = curr._firstFIdx ?? 999999;
    if (idx < prevIdx) {
      curr.nextForecast = f.predicted;
      curr.nextForecastPeriod = f.period;
      curr._firstFIdx = idx;
    }
  }

  // Build list and compute deltas
  const out = [];
  for (const metric of ORDER) {
    if (!byMetric[metric]) continue;
    const { lastActual, nextForecast, nextForecastPeriod } = byMetric[metric];
    if (lastActual == null || nextForecast == null) continue;

    const deltaAbs = nextForecast - lastActual;
    const base = Math.abs(lastActual) > 0 ? Math.abs(lastActual) : null;
    const deltaPct = base ? (deltaAbs / base) * 100 : null;
    const trend = deltaAbs > 0 ? 'up' : deltaAbs < 0 ? 'down' : 'flat';

    out.push({
      metric,
      lastActual,
      nextForecast,
      nextForecastPeriod,
      deltaAbs,
      deltaPct,
      trend
    });
  }
  return out;
}

export default function ForecastKPI({ forecast }) {
  const points = Array.isArray(forecast?.points) ? forecast.points : [];
  const kpis = useMemo(() => computeKpis(points), [points]);

  if (kpis.length === 0) return null;

  return (
    <div className="kpi-grid">
      {kpis.map(k => {
        const pctText =
          k.deltaPct == null
            ? ''
            : `${k.deltaPct > 0 ? '+' : ''}${k.deltaPct.toFixed(1)}%`;

      return (
        <div key={k.metric} className={`kpi-card ${k.trend}`}>
          <div className="kpi-header">
            <div className="kpi-title">{k.metric}</div>
            {pctText && (
              <span className={`trend-chip ${k.trend}`}>
                {k.trend === 'up' ? '▲' : k.trend === 'down' ? '▼' : '—'} {pctText}
              </span>
            )}
          </div>

          <div className="kpi-values">
            <div className="kpi-row">
              <span className="kpi-label">Last actual</span>
              <span className="kpi-number">{formatCurrency(k.lastActual, 1)}</span>
            </div>
            <div className="kpi-row">
              <span className="kpi-label">Next forecast</span>
              <span className="kpi-number">{formatCurrency(k.nextForecast, 1)}</span>
            </div>
            <div className="kpi-row">
              <span className="kpi-label">Change</span>
              <span className="kpi-number">
                {formatCurrency(k.deltaAbs, 1)}
                {k.deltaPct != null ? `  (${k.deltaPct.toFixed(1)}%)` : ''}
              </span>
            </div>
          </div>

          {k.nextForecastPeriod && (
            <div className="kpi-footnote">Period: {k.nextForecastPeriod}</div>
          )}
        </div>
      );
      })}
    </div>
  );
}

